import React, { useState, useEffect } from "react";
import PhysicalDescriptionForm from './PhysicalDescriptionForm';
import SignatureInput from "./SignatureInput";
import { useOnboarding } from '../../Onboarding/OnboardingContext';
import { toast } from 'react-toastify';

const MarkServedForm = ({ servee, location, onSubmit, onCancel, isLoading, currentServerName }) => {
    const { runTour, stepIndex, setStepIndex } = useOnboarding();

    const [serviceDetails, setServiceDetails] = useState({
        method_of_service: "Personal Delivery",
        substitute_name: null,
        documents_served: "Documents Listed in Case File",
        service_date: new Date().toISOString().split('T')[0],
        service_time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        am_pm: new Date().getHours() >= 12 ? 'PM' : 'AM',
    });

    const [physicalDescription, setPhysicalDescription] = useState({});
    const [proofFile, setProofFile] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null); // Now handled by SignatureInput
    const [notaryMode, setNotaryMode] = useState(true); // "upload" | "pad" | ""

    // AUTO-FILL FOR TOUR
    useEffect(() => {
        if (runTour && stepIndex === 9) {
            setPhysicalDescription({
                sex: "Female",
                race: "Caucasian",
                age: "35",
                height: "5'6\"",
                weight: "140 lbs",
                hair: "Brown/Long",
                other_identifiers: "Blue glasses, wearing a business suit."
            });
            setServiceDetails(prev => ({
                ...prev,
                method_of_service: "Personal Delivery",
                substitute_name: "Jane Doe (Self)"
            }));
        }
    }, [runTour, stepIndex]);

    const handleDetailChange = (e) => {
        const { name, value } = e.target;
        setServiceDetails(prev => ({ ...prev, [name]: value || null }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        let finalProof = proofFile;

        // AI: TOUR INTERCEPTION - Create a dummy file if one isn't uploaded so the tour can proceed
        if (runTour && stepIndex === 9 && !finalProof) {
            finalProof = new File(["demo"], "demo-proof.png", { type: "image/png" });
        }

        if (!finalProof) {
            alert("Please upload the final proof photo/document.");
            return;
        }

        const finalPayload = {
            ...serviceDetails,
            ...physicalDescription,
            servee_id: servee.id,
            server_name: currentServerName,
            latitude: location.lat,
            longitude: location.lng,
            notary_mode: notaryMode,
        };

        // Submit to the backend (or intercepted by ServeeActionComponent if runTour is true)
        onSubmit(finalPayload, finalProof, signatureFile);
    };
    
    return (
        <div className="p-4 border border-indigo-300 rounded-md bg-indigo-50 tour-service-details-form">
            <h4 className="font-bold text-indigo-700">Final Service Details for {servee.full_name}</h4>

            <form onSubmit={handleSubmit} className="mt-2 space-y-4">

                {/* Service Method + Substitute */}
                <div className="grid grid-cols-2 gap-3">
                    <input
                        type="text"
                        name="method_of_service"
                        placeholder="Method of Service"
                        value={serviceDetails.method_of_service}
                        onChange={handleDetailChange}
                        required
                        className="p-2 border border-gray-300 rounded"
                    />
                    <input
                        type="text"
                        name="substitute_name"
                        placeholder="Substitute Name (if applicable)"
                        value={serviceDetails.substitute_name || ''}
                        onChange={handleDetailChange}
                        className="p-2 border border-gray-300 rounded"
                    />
                </div>

                <PhysicalDescriptionForm formData={physicalDescription} setFormData={setPhysicalDescription} />

                {/* Proof File */}
                <label className="block">
                    <span className="text-sm font-medium text-gray-700">Final Proof Photo/Document: (Required)</span>
                    <input
                        type="file"
                        onChange={(e) => setProofFile(e.target.files[0])}
                        className="text-sm border p-1 w-full mt-1"
                        required
                    />
                </label>

                {/* Signature Component (Replaces All Signature Logic) */}
                <SignatureInput value={signatureFile} onChange={setSignatureFile} label="Server Signature: (Optional)" />

                <label className="flex items-center gap-1 mb-4">
                    
                    <input
                        type="checkbox"
                        checked={notaryMode === true}
                        onChange={() => setNotaryMode(notaryMode === true ? false : true)}
                    />
                    <span className="text-sm font-medium text-gray-700">Add Notary Signature Block To Affidavit</span>
                    
                </label>

                {/* Submit + Cancel */}
                <div className="flex space-x-2">
                    <button
                        type="submit"
                        disabled={isLoading || (!proofFile && !runTour)}
                        className={`px-4 py-2 rounded text-white tour-submit-served-btn ${isLoading || (!proofFile && !runTour) ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                    >
                        {isLoading ? 'Finalizing...' : 'Mark as Served'}
                    </button>
                    <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-400 text-white rounded">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MarkServedForm;
